import type { UploadTask } from '../types/uploads';
import { transitionUploadTask } from './uploadTaskMachine';

export interface UploadTaskPatch {
  id: string;
  patch: Partial<UploadTask>;
}

function equalJsonValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((value, index) => equalJsonValue(value, right[index]))
    );
  }
  if (left !== null && right !== null && typeof left === 'object' && typeof right === 'object') {
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const leftKeys = Object.keys(leftRecord);
    const rightKeys = Object.keys(rightRecord);
    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every(
        (key) =>
          Object.hasOwn(rightRecord, key) && equalJsonValue(leftRecord[key], rightRecord[key]),
      )
    );
  }
  return false;
}

function patchChangesItem(item: UploadTask, patch: Partial<UploadTask>): boolean {
  return (Object.keys(patch) as Array<keyof UploadTask>).some(
    (key) => !equalJsonValue(item[key], patch[key]),
  );
}

/** Apply many patches in one array pass and preserve the original array when nothing changed. */
export function applyUploadTaskPatches(
  items: readonly UploadTask[],
  patches: readonly UploadTaskPatch[],
): UploadTask[] {
  if (patches.length === 0) return items as UploadTask[];
  const byId = new Map<string, Partial<UploadTask>>();
  for (const { id, patch } of patches) {
    byId.set(id, { ...byId.get(id), ...patch });
  }
  let changed = false;
  const next = items.map((item) => {
    const patch = byId.get(item.id);
    if (!patch || !patchChangesItem(item, patch)) return item;
    changed = true;
    return transitionUploadTask(item, patch);
  });
  return changed ? next : (items as UploadTask[]);
}
